import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Modal, Switch } from 'antd';
import './CookieManagementModal.css';
import { ICookieCategory } from '../../Orchestrator/Types/Interface/ICookieCategory';

interface ICookieManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSavePreferences: (preferences: Record<string, boolean>) => void;
  onAcceptNecessary: () => void;
}

const CookieManagementModal: React.FC<ICookieManagementModalProps> = ({
  isOpen,
  onClose,
  onSavePreferences,
  onAcceptNecessary,
}) => {
  const intl = useIntl();

  const [categories, setCategories] = useState<ICookieCategory[]>([
    {
      id: 'necessary',
      name: intl.formatMessage({ id: 'cookieManagement.necessary', defaultMessage: 'Necessary' }),
      description: intl.formatMessage({
        id: 'cookieManagement.necessaryDesc'
      }),
      required: true,
      enabled: true,
    },
    {
      id: 'statistic',
      name: intl.formatMessage({ id: 'cookieManagement.statistic', defaultMessage: 'Statistic' }),
      description: intl.formatMessage({
        id: 'cookieManagement.statisticDesc'
      }),
      required: false,
      enabled: true,
    },
    {
      id: 'preferences',
      name: intl.formatMessage({ id: 'cookieManagement.preferences', defaultMessage: 'Preferences' }),
      description: intl.formatMessage({
        id: 'cookieManagement.preferencesDesc'
      }),
      required: false,
      enabled: true,
    },
    {
      id: 'marketing',
      name: intl.formatMessage({ id: 'cookieManagement.marketing', defaultMessage: 'Marketing' }),
      description: intl.formatMessage({
        id: 'cookieManagement.marketingDesc'
      }),
      required: false,
      enabled: true,
    },
  ]);

  const handleToggleCategory = (categoryId: string, enabled: boolean) => {
    setCategories(prev =>
      prev.map(cat =>
        cat.id === categoryId ? { ...cat, enabled } : cat
      )
    );
  };

  const handleSavePreferences = () => {
    const preferences = categories.reduce((acc, cat) => {
      acc[cat.id] = cat.enabled;
      return acc;
    }, {} as Record<string, boolean>);
    onSavePreferences(preferences);
  };

  const handleAcceptNecessary = () => {
    setCategories(prev => prev.map(cat => ({ ...cat, enabled: cat.required })));
    onAcceptNecessary();
  };

  return (
    <Modal
      title={intl.formatMessage({ id: 'cookieManagement.title', defaultMessage: 'Manage Cookies' })}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={600}
      className="cookie-management-modal"
      closeIcon={
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      }
    >
      <div className="cookie-management-content">
        <div className="cookie-categories">
          {categories.map((category) => (
            <div key={category.id} className="cookie-category">
              <div className="category-content">
                <h4 className="category-title">{category.name}</h4>
                <p className="category-description">{category.description}</p>
              </div>
              <div className="category-switch">

                <Switch
                  checked={category.enabled}
                  disabled={category.required}
                  onChange={(checked) => handleToggleCategory(category.id, checked)}
                  size="default"
                  className={`cookie-switch ${category.required ? 'disabled' : ''}`}
                />
              </div>

            </div>
          ))}
        </div>

        <div className="modal-actions">
          <button
            className="btn-allow-necessary"
            onClick={handleAcceptNecessary}
          >
            <span className="btn-allow-necessary-text">
              {intl.formatMessage({ id: 'cookieManagement.allowNecessary', defaultMessage: 'Allow Necessary' })}
            </span>
          </button>
          <button
            className="btn-allow-selection"
            onClick={handleSavePreferences}
          >
            <span className="btn-allow-selection-text">
              {intl.formatMessage({ id: 'cookieManagement.allowSelection', defaultMessage: 'Allow selection' })}
            </span>
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CookieManagementModal; 